import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from '@/lib/toast';

type InvalidateTarget =
  | QueryKey
  | {
      queryKey: QueryKey;
      exact?: boolean;
    };

function isInvalidateOptions(
  target: InvalidateTarget
): target is { queryKey: QueryKey; exact?: boolean } {
  return (
    typeof target === 'object' &&
    target !== null &&
    'queryKey' in target &&
    Object.prototype.hasOwnProperty.call(target, 'queryKey')
  );
}

export function useModalMutation<TVariables, TResult>(opts: {
  mutationFn: (variables: TVariables) => Promise<TResult>;
  successMessage: string;
  errorMessage: string;
  invalidate?: InvalidateTarget[];
  onSuccess?: (result: TResult, variables: TVariables) => void | Promise<void>;
  onError?: (error: unknown) => void | boolean;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: opts.mutationFn,
    onSuccess: async (result, variables) => {
      if (opts.invalidate?.length) {
        await Promise.all(
          opts.invalidate.map((target) => {
            const filters = isInvalidateOptions(target) ? target : { queryKey: target };
            return queryClient.invalidateQueries(filters);
          })
        );
      }

      await opts.onSuccess?.(result, variables);
      toast.success(opts.successMessage);
    },
    onError: (error) => {
      const handled = opts.onError?.(error) ?? false;
      if (!handled) toast.error(opts.errorMessage);
    },
  });
}
